/* ExternalLensModal — Loupe externe (Tavily / Perplexity / NOAM offline).
 *
 * Permet à l'utilisateur de taper le nom d'une entité externe (ex: "Coca-Cola
 * Suisse") et d'obtenir une lecture stratégique structurée + un placement
 * dans la grille Asymétries. Bouton "Épingler dans l'Annuaire" pour créer
 * une entrée taggée "externe / non-engagé".
 *
 * Mode dégradé : si aucune clé Tavily/Perplexity n'est configurée, on opère
 * sur NOAM offline (cutoff mai 2025) avec un disclaimer explicite.
 */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { notify, fmtError } from "@/lib/notify";
import {
  Search, X, Loader2, ExternalLink, Pin, AlertTriangle, Globe,
  Sparkles, Target, TrendingUp, ShieldAlert,
} from "lucide-react";

function ZoneBadge({ sub, cen, exp }) {
  const vp = Math.max(cen, exp) - sub / 2;
  let zone = "gray", color = "#71717A", label = "Zone grise";
  if (vp >= 60 && cen >= 60) { zone = "pivot"; color = "#EF9F27"; label = "Pivot"; }
  else if (vp >= 60 && exp >= 60) { zone = "expert"; color = "#1D9E75"; label = "Expert"; }
  else if (vp >= 30) { zone = "emerging"; color = "#378ADD"; label = "Émergent"; }
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
      style={{ background: `${color}20`, color, border: `1px solid ${color}40` }}
      data-testid={`ext-lens-zone-${zone}`}
    >
      <Target className="w-2.5 h-2.5" /> {label}
    </span>
  );
}

export default function ExternalLensModal({ open, onClose }) {
  const [query, setQuery] = useState("");
  const [provider, setProvider] = useState(null); // auto
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [result, setResult] = useState(null);
  const [pinning, setPinning] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    api.get("/workspace/external-lens/status")
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open) {
      setQuery(""); setResult(null); setErr(""); setProvider(null);
    }
  }, [open]);

  const onAnalyze = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setBusy(true); setErr(""); setResult(null);
    try {
      const r = await api.post("/workspace/external-lens/analyze", {
        company_name: query.trim(),
        provider: provider || undefined,
        language: "fr",
      });
      setResult(r.data);
    } catch (e) {
      setErr(fmtError(e, "Analyse impossible."));
    } finally { setBusy(false); }
  };

  const onPin = async () => {
    if (!result?.id) return;
    setPinning(true);
    try {
      const r = await api.post("/workspace/external-lens/pin", { analysis_id: result.id });
      notify.success(`« ${result.company_name} » épinglée dans l'Annuaire.`);
      setResult({ ...result, pinned_to_annuaire: true, annuaire_entity_id: r.data.annuaire_entity_id });
    } catch (e) {
      notify.error(fmtError(e, "Épinglage impossible."));
    } finally { setPinning(false); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="ext-lens-modal"
    >
      <div
        className="noam-card max-w-3xl w-full my-8 p-6 space-y-5"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-[#378ADD]/15 border border-[#378ADD]/30 flex items-center justify-center">
              <Globe className="w-4 h-4 text-[#378ADD]" />
            </div>
            <div>
              <div className="noam-overline">Loupe externe</div>
              <h2 className="text-lg font-bold text-zinc-100">Analyser une entité extérieure</h2>
              <p className="text-[11px] text-zinc-500 mt-0.5">
                Profil + signaux + positionnement Asymétries depuis sources externes.
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white" data-testid="ext-lens-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Disclaimer mode dégradé */}
        {status?.degraded_mode && (
          <div className="flex items-start gap-2 p-3 rounded-md border border-[#EF9F27]/30 bg-[#EF9F27]/5">
            <AlertTriangle className="w-3.5 h-3.5 text-[#EF9F27] flex-shrink-0 mt-0.5" />
            <div className="text-[11px] text-zinc-300">
              <strong className="text-[#EF9F27]">Mode offline (NOAM pur)</strong> — connaissance figée mai 2025, pas de signaux temps réel.
              Pour activer le web : ajoute une clé <code className="px-1 bg-white/10 rounded">TAVILY_API_KEY</code> ou <code className="px-1 bg-white/10 rounded">PERPLEXITY_API_KEY</code> dans <code className="px-1 bg-white/10 rounded">backend/.env</code> puis redémarre le backend.
            </div>
          </div>
        )}

        {/* Form */}
        <form onSubmit={onAnalyze} className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="w-3.5 h-3.5 text-zinc-500 absolute left-2.5 top-2.5" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Coca-Cola Suisse, Nestlé, BlackRock…"
                className="noam-input text-sm pl-8 w-full"
                data-testid="ext-lens-input"
                disabled={busy}
              />
            </div>
            <button
              type="submit"
              disabled={busy || !query.trim()}
              className="btn-primary text-sm disabled:opacity-50"
              data-testid="ext-lens-submit"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {busy ? "Analyse…" : "Analyser"}
            </button>
          </div>
          {status && (
            <div className="flex items-center gap-2 text-[11px]">
              <span className="text-zinc-500">Source :</span>
              {Object.entries(status.providers).map(([k, info]) => (
                <button
                  type="button"
                  key={k}
                  disabled={!info.available || busy}
                  onClick={() => setProvider(k === provider ? null : k)}
                  className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border transition-colors disabled:opacity-30 ${
                    provider === k
                      ? "border-[#378ADD] text-[#378ADD] bg-[#378ADD]/10"
                      : info.available
                        ? "border-white/10 text-zinc-400 hover:border-white/30"
                        : "border-white/5 text-zinc-600 cursor-not-allowed"
                  }`}
                  data-testid={`ext-lens-provider-${k}`}
                  title={info.available ? info.label : `${info.label} — clé non configurée`}
                >
                  {k}
                </button>
              ))}
              {!provider && <span className="text-zinc-600 italic">(auto : {status.default_provider})</span>}
            </div>
          )}
        </form>

        {err && (
          <div className="flex items-start gap-2 p-2 rounded-md border border-[#E24B4A]/30 bg-[#E24B4A]/10 text-[#FCA5A5] text-[11px]">
            <AlertTriangle className="w-3 h-3 mt-0.5" /> <span>{err}</span>
          </div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4 pt-2 border-t border-white/5" data-testid="ext-lens-result">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-base font-bold text-zinc-100">{result.company_name}</h3>
                  {result.grid && (
                    <ZoneBadge
                      sub={result.grid.substituability}
                      cen={result.grid.centrality}
                      exp={result.grid.expertise}
                    />
                  )}
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5 uppercase tracking-wider">
                  Source : {result.provider} · {new Date(result.created_at).toLocaleString("fr-CH")}
                </div>
              </div>
              {!result.pinned_to_annuaire ? (
                <button
                  onClick={onPin}
                  disabled={pinning}
                  className="btn-ghost text-xs disabled:opacity-50"
                  data-testid="ext-lens-pin"
                >
                  {pinning ? <Loader2 className="w-3 h-3 animate-spin" /> : <Pin className="w-3 h-3" />}
                  Épingler dans l'Annuaire
                </button>
              ) : (
                <span className="text-[10px] text-[#1D9E75] flex items-center gap-1" data-testid="ext-lens-pinned">
                  <Pin className="w-3 h-3" /> Épinglée
                </span>
              )}
            </div>

            {result.profile?.description && (
              <div>
                <div className="noam-overline mb-1">Profil</div>
                <p className="text-xs text-zinc-300 leading-relaxed">{result.profile.description}</p>
              </div>
            )}

            {result.profile?.signals?.length > 0 && (
              <div>
                <div className="noam-overline mb-1.5">Signaux récents</div>
                <ul className="space-y-1.5">
                  {result.profile.signals.slice(0, 6).map((s, i) => (
                    <li key={i} className="text-[11px] text-zinc-300 flex gap-2">
                      <span className="text-[#378ADD] flex-shrink-0">›</span>
                      <span>{s}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {result.grid && (
              <div className="grid md:grid-cols-3 gap-2">
                <Metric label="Substituabilité" value={result.grid.substituability} accent="#E24B4A" />
                <Metric label="Centralité" value={result.grid.centrality} accent="#EF9F27" />
                <Metric label="Expertise" value={result.grid.expertise} accent="#1D9E75" />
              </div>
            )}

            {result.grid?.rationale && (
              <p className="text-[11px] text-zinc-400 italic border-l-2 border-[#378ADD]/40 pl-3">
                {result.grid.rationale}
              </p>
            )}

            {result.grid?.negotiation_levers?.length > 0 && (
              <div>
                <div className="noam-overline mb-1.5 flex items-center gap-1.5">
                  <TrendingUp className="w-3 h-3 text-[#1D9E75]" /> Leviers de négociation
                </div>
                <ul className="space-y-1">
                  {result.grid.negotiation_levers.map((lev, i) => (
                    <li key={i} className="text-[11px] text-zinc-300">→ {lev}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.grid?.risks?.length > 0 && (
              <div>
                <div className="noam-overline mb-1.5 flex items-center gap-1.5">
                  <ShieldAlert className="w-3 h-3 text-[#E24B4A]" /> Risques identifiés
                </div>
                <ul className="space-y-1">
                  {result.grid.risks.map((r, i) => (
                    <li key={i} className="text-[11px] text-zinc-300">⚠ {r}</li>
                  ))}
                </ul>
              </div>
            )}

            {result.profile?.sources?.length > 0 && (
              <div>
                <div className="noam-overline mb-1.5">Sources ({result.profile.sources.length})</div>
                <ul className="space-y-1">
                  {result.profile.sources.slice(0, 6).map((s, i) => (
                    <li key={i} className="text-[11px]">
                      <a
                        href={s.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#378ADD] hover:underline inline-flex items-center gap-1"
                      >
                        <ExternalLink className="w-2.5 h-2.5" /> {s.title || s.url}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Metric({ label, value, accent }) {
  return (
    <div className="noam-card p-3" data-testid={`ext-lens-metric-${label.toLowerCase()}`}>
      <div className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className="flex items-baseline gap-1">
        <span className="text-xl font-bold" style={{ color: accent }}>{value}</span>
        <span className="text-zinc-600 text-[10px]">/100</span>
      </div>
      <div className="mt-1 h-1 rounded-full bg-white/5 overflow-hidden">
        <div className="h-full" style={{ width: `${value}%`, background: accent }} />
      </div>
    </div>
  );
}
